import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SocketService } from 'src/app/shared/services/socket.service';
import {
  ConnectedUser,
  ConnectedUsersService,
  OnlineUsersResponse,
} from '../../services/connected-users.service';

type FilterType = 'all' | 'VE' | 'CL' | 'operators';

@Component({
  selector: 'app-connected-users',
  templateUrl: './connected-users.component.html',
  styleUrls: ['./connected-users.component.sass'],
})
export class ConnectedUsersComponent implements OnInit, OnDestroy {
  private subs = new Subscription();

  allUsers: ConnectedUser[] = [];
  filteredUsers: ConnectedUser[] = [];

  totalConnections = 0;
  uniqueUsers = 0;
  clientsVE = 0;
  clientsCL = 0;
  operators = 0;
  stage = '';

  activeFilter: FilterType = 'all';
  expandedCardId: number | null = null;
  socketConnected = false;

  constructor(
    private connectedUsersService: ConnectedUsersService,
    private socketService: SocketService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.socketService.isConnected$().subscribe((v) => (this.socketConnected = v)),
    );

    this.subs.add(
      this.connectedUsersService.getAll().subscribe({
        next: (data) => this.updateData(data),
      }),
    );

    this.subs.add(
      this.socketService.onUsersUpdated$().subscribe((data) => this.updateData(data)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private updateData(data: OnlineUsersResponse): void {
    this.allUsers = data.users;
    this.totalConnections = data.totalConnections;
    this.uniqueUsers = data.uniqueUsers;
    this.clientsVE = data.clientsVE;
    this.clientsCL = data.clientsCL;
    this.operators = data.operators;
    this.stage = data.stage;
    this.applyFilter();
  }

  setFilter(filter: FilterType): void {
    this.activeFilter = filter;
    this.applyFilter();
  }

  private applyFilter(): void {
    switch (this.activeFilter) {
      case 'VE':
        this.filteredUsers = this.allUsers.filter(
          (u) => !u.isOperator && u.tipoId === 'VE',
        );
        break;
      case 'CL':
        this.filteredUsers = this.allUsers.filter(
          (u) => !u.isOperator && u.tipoId !== 'VE',
        );
        break;
      case 'operators':
        this.filteredUsers = this.allUsers.filter((u) => u.isOperator);
        break;
      default:
        this.filteredUsers = [...this.allUsers];
    }
  }

  getTimeAgo(date: string): string {
    const diffMs = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m`;
    return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  }

  getEarliestConnection(user: ConnectedUser): string {
    if (!user.connections?.length) return '';
    const sorted = [...user.connections].sort(
      (a, b) => new Date(a.connectedAt).getTime() - new Date(b.connectedAt).getTime(),
    );
    return sorted[0].connectedAt;
  }

  getDeviceIcon(deviceType: string): string {
    if (deviceType === 'mobile') return 'phone_android';
    if (deviceType === 'tablet') return 'tablet';
    return 'computer';
  }

  getPrimaryConnection(user: ConnectedUser) {
    return user.connections?.[0];
  }

  getUserTypeBadge(user: ConnectedUser): { label: string; color: string } {
    if (user.isOperator) return { label: 'Operador', color: '#7c3aed' };
    if (user.tipoId === 'VE') return { label: 'Venezuela', color: '#0369a1' };
    return { label: 'Chile', color: '#b45309' };
  }

  toggleCard(idx: number): void {
    this.expandedCardId = this.expandedCardId === idx ? null : idx;
  }
}
